import { useEffect, useRef, useState } from 'react'
import { categoryLabel, formatMoney, imageUrl, requestApi, serviceCover, type NailService } from './catalog'

interface Props {
  productId: string
  storeId?: string
  onClose: () => void
  onBook: (product: NailService) => void
  /** 实时刷新令牌：变化时重新拉取商品详情。 */
  refreshToken?: number
}

export function ProductDetails({ productId, storeId, onClose, onBook, refreshToken = 0 }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [product, setProduct] = useState<NailService | null>(null)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    const dialog = dialogRef.current
    const previousOverflow = document.body.style.overflow
    dialog?.showModal()
    document.body.style.overflow = 'hidden'
    return () => {
      dialog?.close()
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setProduct(null)
    setError('')
    void requestApi<NailService>(`/services/${encodeURIComponent(productId)}${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ''}`, { signal: controller.signal })
      .then((item) => {
        if (!controller.signal.aborted) setProduct(item)
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : '商品暂时无法查看，请稍后重试')
      })
    return () => controller.abort()
  }, [productId, storeId, retry, refreshToken])

  const detailImages = (product?.detailImageUrls ?? []).map(imageUrl).filter(Boolean)
  const cover = product ? serviceCover(product) || detailImages[0] : ''

  return (
    <dialog ref={dialogRef} className="product-dialog" aria-labelledby="product-title" onCancel={(event) => { event.preventDefault(); onClose() }} onClick={(event) => {
      if (event.target !== event.currentTarget) return
      const bounds = event.currentTarget.getBoundingClientRect()
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose()
    }}>
      <header className="product-dialog__header"><span>TINGFENGYU · 商品详情</span><button type="button" aria-label="关闭商品详情" onClick={onClose}>×</button></header>
      {!product && <div className="product-dialog__status" role="status"><h2 id="product-title">{error ? '暂时无法查看商品' : '正在加载商品…'}</h2>{error && <><p>{error}</p><button type="button" className="button button--light" onClick={() => setRetry((value) => value + 1)}>重新加载</button></>}</div>}
      {product && <>
        <div className="product-dialog__overview">
          <div className="product-dialog__cover">{cover ? <a href={cover} target="_blank" rel="noreferrer" aria-label={`查看${product.name}完整原图`}><img src={cover} alt={product.name} /></a> : <div className="product-placeholder"><span>汀</span><small>商品图片待补充</small></div>}</div>
          <div className="product-dialog__summary"><span className="eyebrow">{categoryLabel(product)}</span><h2 id="product-title">{product.name}</h2><p>{product.description}</p><div className="product-dialog__price"><strong>{formatMoney(product.price)}</strong><span>约 {product.durationMinutes} 分钟</span></div><p className="product-dialog__note">到店服务 · 选择适合自己的款式，预约时间到店体验。</p></div>
        </div>
        <section className="product-dialog__details" aria-labelledby="product-details-heading"><h3 id="product-details-heading">商品介绍</h3><p className="product-dialog__description">{product.detailDescription || product.description || '具体服务内容可在预约前联系门店了解。'}</p>{detailImages.length > 0 && <div className="product-dialog__posters">{detailImages.map((url, index) => <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer" aria-label={`查看${product.name}第 ${index + 1} 张详情原图`}><img src={url} alt={`${product.name}详情图 ${index + 1}`} loading="lazy" /></a>)}<small>点击图片可查看完整原图</small></div>}</section>
        <div className="product-dialog__booking"><div><small>到店服务</small><strong>{formatMoney(product.price)}</strong></div><button className="button button--dark" type="button" disabled={!product.enabled} onClick={() => onBook(product)}>{product.enabled ? '预约此商品' : '商品已下架'}</button></div>
      </>}
    </dialog>
  )
}
